import "./curve-editor.css";

// CurveEditor — recessed display hosting a curve with draggable handles.
// Generic enough to host ADSR envelopes, EQ band-position dragging, LFO
// shaping, or anything else that's "drag points to reshape a curve."
//
// The component owns:
//   - The recessed-well visual (matches Display)
//   - DPR-safe canvas with the configured grid + frame
//   - Hit-testing on each Point and pointer drag with axis constraints
//   - Smooth curve drawing through the points (caller supplies the path
//     function, since envelope curves and EQ curves interpolate
//     differently)
//
// The caller defines the Points (each with normalized 0..1 coordinates,
// drag-axis constraints, and a tracer-or-not flag), supplies an `onChange`
// callback that gets a fresh set of point positions, and supplies a draw
// function that draws the curve given the current points.

export interface CurvePoint {
  /** Normalized 0..1 position along the X axis. */
  x: number;
  /** Normalized 0..1 position along the Y axis (0 = bottom, 1 = top). */
  y: number;
  /** Axis the user can drag this point along. Use to e.g. keep the
   *  sustain handle horizontally locked. */
  drag: "x" | "y" | "xy" | "none";
  /** Optional clamp range for x (defaults to [0, 1]). */
  xRange?: [number, number];
  /** Optional clamp range for y (defaults to [0, 1]). */
  yRange?: [number, number];
  /** When true, this point's effective x range is further narrowed during
   *  drag by the x positions of its array neighbours — so neighbouring
   *  handles maintain a strict left-to-right order. Used for ADSR-style
   *  envelopes where attack < decay < release must always hold.
   *  Non-x-draggable handles (e.g. mid-segment curvature handles) are
   *  skipped when finding the neighbours, so the order applies between
   *  the *main* handles only. */
  lockNeighborsX?: boolean;
  /** Visual size of the handle. "small" renders ~60% radius with the same
   *  hit-padding-shrunk hit area — used for secondary, curvature-shaping
   *  mid-handles to keep them visually subordinate to main handles. */
  size?: "main" | "small";
  /** Optional label for accessibility / tooltip. */
  label?: string;
}

export type CurveDrawFn = (
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  points: CurvePoint[],
) => void;

export interface CurveEditorOptions {
  container: HTMLElement;
  /** Initial / desktop CSS width in px. In `fluid` mode this is treated
   *  as the *maximum* width; the well shrinks to fit narrower containers. */
  width: number;
  height: number;
  points: CurvePoint[];
  draw: CurveDrawFn;
  onChange?: (points: CurvePoint[]) => void;
  /** Optional caption rendered below the well. */
  label?: string;
  /** Hide the draggable handles (useful for read-only preview displays). */
  readOnly?: boolean;
  /** When true, the well's CSS width becomes `min(width, 100%)` and a
   *  ResizeObserver keeps the canvas re-drawn whenever the actual
   *  rendered CSS width changes. Used by modules that live in a
   *  responsive panel layout (mobile breakpoint, etc.). */
  fluid?: boolean;
}

const HANDLE_R = 6;
const HANDLE_R_SMALL = 3.5;
const HANDLE_HIT_PADDING = 4;

function handleRadius(p: CurvePoint): number {
  return p.size === "small" ? HANDLE_R_SMALL : HANDLE_R;
}

export class CurveEditor {
  private root: HTMLDivElement;
  private well: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private points: CurvePoint[];
  private drawFn: CurveDrawFn;
  private onChange: ((points: CurvePoint[]) => void) | undefined;
  private readOnly: boolean;
  private cssW: number;
  private cssH: number;
  private fluid: boolean;
  private resizeObserver: ResizeObserver | null = null;

  private dragIndex = -1;
  private dragPointerId = -1;
  private hoverIndex = -1;
  private tracerT = -1; // 0..1, -1 = no tracer drawn

  constructor(opts: CurveEditorOptions) {
    this.cssW = opts.width;
    this.cssH = opts.height;
    this.fluid = opts.fluid ?? false;
    this.points = opts.points.map((p) => ({ ...p }));
    this.drawFn = opts.draw;
    if (opts.onChange) this.onChange = opts.onChange;
    this.readOnly = opts.readOnly ?? false;

    this.root = document.createElement("div");
    this.root.className = "curve-cell";
    if (this.fluid) {
      // Mark both the root and the well as fluid. The root needs to be
      // `display: flex; width: 100%` (CSS) so it stretches to fill its
      // parent's row instead of shrinking to its content — without this,
      // once a viewport narrows the inline-flex cell's intrinsic width
      // shrinks with it and never grows back when the viewport widens
      // again (cf. fluid-cells-shrink-stuck bug).
      this.root.classList.add("is-fluid");
    }

    this.well = document.createElement("div");
    this.well.className = "curve-well";
    if (this.fluid) {
      // Cap the well at the desktop width but let it shrink to its
      // container on mobile / narrow layouts.
      this.well.classList.add("is-fluid");
      this.well.style.width = "100%";
      this.well.style.maxWidth = `${this.cssW}px`;
    } else {
      this.well.style.width = `${this.cssW}px`;
    }
    this.well.style.height = `${this.cssH}px`;

    this.canvas = document.createElement("canvas");
    this.canvas.style.width = this.fluid ? "100%" : `${this.cssW}px`;
    this.canvas.style.height = `${this.cssH}px`;
    if (!this.readOnly) {
      this.canvas.style.cursor = "default";
    }
    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("curve: 2d context unavailable");
    this.ctx = ctx;
    this.well.append(this.canvas);
    this.root.append(this.well);

    if (opts.label) {
      const lbl = document.createElement("span");
      lbl.className = "label";
      lbl.textContent = opts.label;
      this.root.append(lbl);
    }
    opts.container.append(this.root);

    if (!this.readOnly) this.attachInteraction();

    // In fluid mode, redraw whenever the well's actual rendered width
    // changes — viewport resize, container collapsing, etc. The DPR
    // scaling inside `draw()` already reads `cssW` per call, so updating
    // that field here is enough to keep the canvas pixel-buffer
    // matched to the layout.
    if (this.fluid) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const e of entries) {
          const w = e.contentRect.width;
          if (w > 0 && Math.abs(w - this.cssW) > 0.5) {
            this.cssW = w;
            this.draw();
          }
        }
      });
      this.resizeObserver.observe(this.well);
    }

    this.draw();
  }

  /** Replace the curve's draggable points. Triggers a redraw and the
   * `onChange` callback. */
  setPoints(points: CurvePoint[]): void {
    this.points = points.map((p) => ({ ...p }));
    this.onChange?.(this.points);
    this.draw();
  }

  getPoints(): CurvePoint[] {
    return this.points.map((p) => ({ ...p }));
  }

  /** Set a tracer position along the curve in 0..1. -1 hides the tracer. */
  setTracer(t: number): void {
    if (t === this.tracerT) return;
    this.tracerT = t;
    this.draw();
  }

  /** Force a redraw without mutating points. Use when the curve's *shape*
   *  parameters change (e.g. filter mode swapping between LP and HP) but
   *  the draggable handles haven't moved. */
  redraw(): void {
    this.draw();
  }

  destroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.root.remove();
  }

  // ----- interaction -----

  private attachInteraction(): void {
    this.canvas.style.pointerEvents = "auto";
    this.canvas.addEventListener("pointerdown", (ev) => {
      const idx = this.hitTest(ev.offsetX, ev.offsetY);
      if (idx < 0) return;
      ev.preventDefault();
      this.canvas.setPointerCapture(ev.pointerId);
      this.dragIndex = idx;
      this.dragPointerId = ev.pointerId;
      this.draw();
    });
    this.canvas.addEventListener("pointermove", (ev) => {
      if (this.dragIndex < 0) {
        const idx = this.hitTest(ev.offsetX, ev.offsetY);
        if (idx !== this.hoverIndex) {
          this.hoverIndex = idx;
          this.canvas.style.cursor = idx >= 0 ? "grab" : "default";
          this.draw();
        }
        return;
      }
      const p = this.points[this.dragIndex]!;
      if (p.drag === "x" || p.drag === "xy") {
        const tx = clamp(ev.offsetX / this.cssW, 0, 1);
        let [xLo, xHi] = p.xRange ?? [0, 1];
        if (p.lockNeighborsX) {
          // Walk outward to find the nearest neighbours that themselves
          // care about x — skipping curvature mid-handles (drag="y") so
          // the order applies between the main handles only.
          let prev: CurvePoint | undefined;
          for (let i = this.dragIndex - 1; i >= 0; i--) {
            const q = this.points[i]!;
            if (q.drag === "x" || q.drag === "xy") {
              prev = q;
              break;
            }
          }
          let next: CurvePoint | undefined;
          for (let i = this.dragIndex + 1; i < this.points.length; i++) {
            const q = this.points[i]!;
            if (q.drag === "x" || q.drag === "xy") {
              next = q;
              break;
            }
          }
          if (prev) xLo = Math.max(xLo, prev.x);
          if (next) xHi = Math.min(xHi, next.x);
        }
        p.x = clamp(tx, xLo, xHi);
      }
      if (p.drag === "y" || p.drag === "xy") {
        const ty = clamp(1 - ev.offsetY / this.cssH, 0, 1);
        const [yLo, yHi] = p.yRange ?? [0, 1];
        p.y = clamp(ty, yLo, yHi);
      }
      this.canvas.style.cursor = "grabbing";
      this.onChange?.(this.points);
      this.draw();
    });
    const endDrag = (ev: PointerEvent): void => {
      if (this.dragIndex < 0) return;
      this.canvas.releasePointerCapture(this.dragPointerId);
      this.dragIndex = -1;
      this.dragPointerId = -1;
      this.canvas.style.cursor = this.hoverIndex >= 0 ? "grab" : "default";
      this.draw();
      void ev;
    };
    this.canvas.addEventListener("pointerup", endDrag);
    this.canvas.addEventListener("pointercancel", endDrag);
  }

  private hitTest(x: number, y: number): number {
    // Iterate in reverse so smaller / later-drawn handles are picked first
    // when they overlap (mid-handles often sit between main handles).
    for (let i = this.points.length - 1; i >= 0; i--) {
      const p = this.points[i]!;
      if (p.drag === "none") continue;
      const px = p.x * this.cssW;
      const py = (1 - p.y) * this.cssH;
      const dx = x - px;
      const dy = y - py;
      const r = handleRadius(p) + HANDLE_HIT_PADDING;
      if (dx * dx + dy * dy <= r * r) return i;
    }
    return -1;
  }

  // ----- drawing -----

  private draw(): void {
    const dpr = window.devicePixelRatio || 1;
    const px = this.cssW * dpr;
    const py = this.cssH * dpr;
    if (this.canvas.width !== px || this.canvas.height !== py) {
      this.canvas.width = px;
      this.canvas.height = py;
    }
    const ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, this.cssW, this.cssH);

    // Faint grid lines so the curve has spatial reference.
    ctx.strokeStyle = "rgba(56, 189, 248, 0.06)";
    ctx.lineWidth = 1;
    for (let i = 1; i < 4; i++) {
      const yy = (this.cssH * i) / 4;
      ctx.beginPath();
      ctx.moveTo(0, yy);
      ctx.lineTo(this.cssW, yy);
      ctx.stroke();
    }
    for (let i = 1; i < 4; i++) {
      const xx = (this.cssW * i) / 4;
      ctx.beginPath();
      ctx.moveTo(xx, 0);
      ctx.lineTo(xx, this.cssH);
      ctx.stroke();
    }

    // Caller draws the curve itself — they know how to interpolate.
    this.drawFn(ctx, this.cssW, this.cssH, this.points);

    // Tracer dot — if set, the caller's draw already left the curve in
    // place, so we just sample the y at the requested x. Since we don't
    // know the curve's interpolation, we let the caller embed the tracer
    // in `drawFn` if they want a precise tracer. As a fallback the editor
    // draws a vertical guide line at the tracer's t.
    if (this.tracerT >= 0 && this.tracerT <= 1) {
      ctx.save();
      ctx.strokeStyle = "rgba(125, 211, 252, 0.4)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      const tx = this.tracerT * this.cssW;
      ctx.moveTo(tx, 0);
      ctx.lineTo(tx, this.cssH);
      ctx.stroke();
      ctx.restore();
    }

    // Draggable handles, drawn on top of the curve. Small handles render
    // with slightly less aggressive shadow so they read as secondary.
    if (!this.readOnly) {
      for (let i = 0; i < this.points.length; i++) {
        const p = this.points[i]!;
        if (p.drag === "none") continue;
        const isHover = this.hoverIndex === i;
        const isDrag = this.dragIndex === i;
        const x = p.x * this.cssW;
        const y = (1 - p.y) * this.cssH;
        const small = p.size === "small";
        ctx.save();
        ctx.shadowColor = "rgba(125, 211, 252, 0.9)";
        const baseBlur = small ? 3 : 5;
        const hoverBlur = small ? 6 : 9;
        const dragBlur = small ? 10 : 14;
        ctx.shadowBlur = isDrag ? dragBlur : isHover ? hoverBlur : baseBlur;
        ctx.fillStyle = isDrag
          ? "#e0f2fe"
          : isHover
            ? "#bae6fd"
            : "#7dd3fc";
        ctx.beginPath();
        ctx.arc(x, y, handleRadius(p), 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
